import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

interface TransactionStatusDisplayProps {
  transaction: any;
  status: any;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  onCopyAddress: (address: string) => void;
  isAutoRefreshEnabled: boolean;
}

const statusConfig: Record<string, { label: string; color: string }> = {
  new: { label: 'NEW', color: '#ffbb00' },
  waiting: { label: 'WAITING FOR DEPOSIT', color: '#ffbb00' },
  confirming: { label: 'CONFIRMING', color: '#e0e0e0' },
  exchanging: { label: 'EXCHANGING', color: '#e0e0e0' },
  sending: { label: 'SENDING FUNDS', color: '#e0e0e0' },
  finished: { label: 'COMPLETED', color: '#00cc44' },
  completed: { label: 'COMPLETED', color: '#00cc44' },
  done: { label: 'COMPLETED', color: '#00cc44' },
  failed: { label: 'FAILED', color: '#ff4444' },
  refunding: { label: 'REFUNDING', color: '#ffbb00' },
  expired: { label: 'EXPIRED', color: '#ff4444' }
};

const REFRESH_INTERVAL = 10;

export const TransactionStatusDisplay: React.FC<TransactionStatusDisplayProps> = ({
  transaction,
  status,
  isLoading,
  error,
  onRefresh,
  onCopyAddress,
  isAutoRefreshEnabled
}) => {
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? REFRESH_INTERVAL : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isLoading) setCountdown(REFRESH_INTERVAL);
  }, [isLoading]);

  if (!transaction) return null;

  const currentStatus = status?.status || transaction.status;
  const normalizedStatus = (currentStatus || '').toLowerCase();
  const statusInfo = statusConfig[normalizedStatus] || { label: 'UNKNOWN', color: '#e0e0e0' };

  const isCompleted = ['finished', 'completed', 'done'].includes(normalizedStatus);
  const isFailed = normalizedStatus === 'failed';
  const isPending = ['new', 'waiting', 'confirming', 'exchanging', 'sending'].includes(normalizedStatus);
  const needsDeposit = isPending && !status?.depositReceived;

  const payinAddress = transaction?.payinAddress || transaction?.changeNowData?.payinAddress || transaction?.changeNowData?.payin_address;

  return (
    <div className="terminal-container">
      {/* Header */}
      <div className="terminal-status-header" style={{ borderColor: statusInfo.color }}>
        <div className="terminal-status-label" style={{ color: statusInfo.color }}>
          TRANSACTION STATUS
        </div>
        <div className="terminal-status-title" style={{ color: statusInfo.color }}>
          {statusInfo.label}
        </div>
        <div className="terminal-status-subtitle">
          ID: {transaction.id}
        </div>
        <div className="terminal-status-subtitle">
          REFRESH IN: {countdown}s | AUTO-REFRESH: {isAutoRefreshEnabled ? 'ON' : 'OFF'}
        </div>
      </div>

      {/* Status messages */}
      {needsDeposit && (
        <div className="terminal-status-box" style={{ borderColor: '#ffbb00' }}>
          <div style={{ color: '#ffbb00' }}>
            ! WAITING FOR YOUR DEPOSIT...
          </div>
        </div>
      )}

      {isCompleted && (
        <div className="terminal-status-box" style={{ borderColor: '#e0e0e0' }}>
          <div style={{ color: '#e0e0e0' }}>
            * EXCHANGE COMPLETED SUCCESSFULLY
          </div>
        </div>
      )}

      {isFailed && (
        <div className="terminal-status-box" style={{ borderColor: '#ff4444' }}>
          <div style={{ color: '#ff4444' }}>
            ! EXCHANGE FAILED. CONTACT SUPPORT.
          </div>
        </div>
      )}

      {/* Payin Address */}
      {payinAddress && (
        <div className="terminal-status-box">
          <div className="terminal-status-label">
            {needsDeposit ? '&gt; DEPOSIT ADDRESS:' : '&gt; DEPOSIT ADDRESS (USED):'}
          </div>
          <div className="terminal-qr-container">
            {/* QR Code */}
            <div className="terminal-qr-code">
              <QRCodeSVG
                value={payinAddress}
                size={120}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
            </div>

            {/* Address */}
            <div style={{ flex: 1, minWidth: '250px' }}>
              <div className="terminal-address-box">
                {payinAddress}
              </div>
              <button
                onClick={() => onCopyAddress(payinAddress)}
                className="terminal-copy-btn"
              >
                [ COPY ADDRESS ]
              </button>
              <div className="terminal-amount-info">
                ! SEND EXACTLY: {transaction.fromAmount} {transaction.fromCurrency?.toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Details */}
      <div className="terminal-status-box">
        <div className="terminal-status-label">&gt; TRANSACTION DETAILS:</div>
        <div className="terminal-details">
          <div>ID: {transaction.id}</div>
          <div>EXCHANGE: {transaction.fromAmount} {transaction.fromCurrency?.toUpperCase()} → {transaction.toCurrency?.toUpperCase()}</div>
          {transaction.toAmount && (
            <div style={{ color: '#e0e0e0' }}>
              EST. RECEIVE: {transaction.toAmount} {transaction.toCurrency?.toUpperCase()}
            </div>
          )}
          {status?.depositReceived && (
            <div style={{ color: '#e0e0e0' }}>
              [*] DEPOSIT RECEIVED
            </div>
          )}
          {status?.payoutSent && (
            <div style={{ color: '#e0e0e0' }}>
              [*] PAYMENT SENT
            </div>
          )}
          {!status?.depositReceived && isPending && (
            <div style={{ color: '#ffbb00' }}>
              [ ] WAITING FOR DEPOSIT
            </div>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="terminal-status-box" style={{ borderColor: '#ff4444' }}>
          <div style={{ color: '#ff4444' }}>
            STATUS ERROR: {error}
          </div>
        </div>
      )}

      {/* Refresh button */}
      <button
        onClick={onRefresh}
        disabled={isLoading}
        className="terminal-button terminal-button-full"
      >
        {isLoading ? 'REFRESHING...' : '[ REFRESH STATUS ]'}
      </button>

      {/* Warning */}
      {needsDeposit && payinAddress && (
        <div className="terminal-alert">
          ! IMPORTANT: SEND ONLY {transaction.fromCurrency?.toUpperCase()} TO THIS ADDRESS.
          SENDING ANY OTHER CURRENCY MAY RESULT IN PERMANENT LOSS.
        </div>
      )}
    </div>
  );
};
